import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import {
  emptyProductTypeForm,
  fromProductType,
  toProductTypeCreate,
  toProductTypeUpdate,
} from '../api/product-type-mappers';
import { useCreateProductType, useUpdateProductType } from '../hooks/use-product-type-mutations';
import { useProductType } from '../hooks/use-product-types';

import { ProductTypeDangerZone } from './product-type-danger-zone';
import { ProductTypeForm } from './product-type-form';

/** Sits under the catalog setup header and tabs, so it is a second-level heading. */
function EditorHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  const { t } = useTranslation('productTypes');
  return (
    <div className="flex flex-col gap-2">
      <Button asChild variant="ghost" size="sm" className="-ml-2 self-start">
        <Link to="/org/catalog-setup/product-types">
          <ArrowLeft aria-hidden />
          {t('backToList')}
        </Link>
      </Button>
      <h2 className="text-lg font-semibold">{title}</h2>
      {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
    </div>
  );
}

export function ProductTypeCreate() {
  const { t } = useTranslation('productTypes');
  const navigate = useNavigate();
  const create = useCreateProductType();

  return (
    <div className="flex flex-col gap-4">
      <EditorHeader title={t('new')} description={t('newDescription')} />
      <ProductTypeForm
        productType={null}
        defaultValues={emptyProductTypeForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const row = await create.mutateAsync(toProductTypeCreate(values));
          await navigate({
            to: '/org/catalog-setup/product-types/$productTypeId',
            params: { productTypeId: row.id },
            replace: true,
          });
        }}
      />
    </div>
  );
}

export function ProductTypeEdit({ productTypeId }: { productTypeId: string }) {
  const { t } = useTranslation('productTypes');
  const navigate = useNavigate();
  // Prefetched by the route loader, so this normally resolves from cache.
  const query = useProductType(productTypeId);
  const update = useUpdateProductType(productTypeId);
  const router = useRouter();
  const productType = query.data;

  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!productType) return <Skeleton className="h-64" />;

  return (
    <div className="flex flex-col gap-4">
      <EditorHeader
        title={productType.name}
        description={<code className="text-xs">{productType.slug}</code>}
      />
      <ProductTypeForm
        key={productType.updatedAt}
        productType={productType}
        defaultValues={fromProductType(productType)}
        isSubmitting={update.isPending}
        onSubmit={async (values) => {
          const updated = await update.mutateAsync(toProductTypeUpdate(values, productType));
          // A changed SKU code never rewrites SKUs; say how many still carry the old one.
          if (updated.affectedVariantCount > 0) {
            toast.message(t('skuCode.affected', { count: updated.affectedVariantCount }));
          }
          // The crumb is loader data; a rename shows there only once the loader runs again.
          void router.invalidate();
        }}
        dangerZone={
          <Can permission="attributes.write">
            <ProductTypeDangerZone
              productType={productType}
              onDeleted={() => {
                // The type is gone; a dirty form must not keep us on its page.
                releaseSaveBar();
                void navigate({ to: '/org/catalog-setup/product-types' });
              }}
            />
          </Can>
        }
      />
    </div>
  );
}
